import { GCInstant as platform } from 'src/lib/gcinstant';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user, now } from 'src/StateObserver';
import { track } from 'src/lib/analytics/utils';
import { openPopupPromise, closePopup } from 'src/lib/popups/popupOpenClose';
import PopupMonitor from 'src/game/logic/PopupMonitor';
import i18n from 'src/lib/i18n/i18n';
import { captureGenericError } from 'src/lib/sentry';
import {
  isJankenChallengeExpired,
  isResolvedChallenge,
  jankenIsAvailable,
  jankenGatePasses,
  jankenGateFailure,
  getJankenPendingResults,
  canGrantJankenReward,
  jankenHasSeenTutorial,
} from 'src/replicant/janken/janken.getters';
import { getPlayerProfile } from 'src/replicant/profile/profile.getters';
import {
  getCurrentPetState,
  hasPermanentStatusEffect,
} from 'src/replicant/fate/fate.getters';
import {
  deriveJankenSenderMove,
  deriveJankenOutcome,
  rollJankenReward,
  parseJankenChallengeTimestamp,
} from 'src/replicant/janken/janken.utils';
import * as Pet from 'src/replicant/pet';
import type { PopupJankenClose } from './popups/PopupJanken';
import { getJankenPopupInstance } from './popups/PopupJanken';
import type { JankenOpenOrigin } from './janken.analytics.schema';
import {
  JANKEN_CHALLENGE_EXPIRY_MS,
  type JankenOutcome,
  type JankenMove,
} from 'src/replicant/janken/janken.ruleset';
import type { CareItemType, CareItemID } from 'src/replicant/care/care.ruleset';
import type { PetSpeciesID } from 'src/replicant/pet';
import { petSpeciesTuple } from 'src/replicant/pet/pet.ruleset';
import { purchaseCareItemSilent } from 'src/game/care/care.sequence';
import { JANKEN_SHARE_SUBFEATURES } from './janken.share';

/*
 * ==========================================================================
 * janken.sequence.ts — Orchestration for all janken (rock-paper-scissors) flows.
 *
 * STATELESS: All flows are driven by entry data or pending results — no
 * intermediate state is saved. If the user dismisses, they re-enter from
 * the original link. Sender pending results are created server-side by the
 * jankenChallengeResolved postMessage and claimed via HUD or OA entry.
 *
 * ENTRY SEQUENCES (launch + HUD)
 *   jankenInviteEntrySequence(origin) — Invite link: resolve challenge (receiver)
 *   jankenResultEntrySequence(origin) — OA result: claim matching pending (sender)
 *   jankenSenderSequence(origin)      — Create a new challenge (tutorial → share)
 *   jankenHudSequence()               — HUD: entry flows → drain pending → sender
 *
 * ENTRY DATA HELPERS
 *   jankenIsFromEntryData()           — Entry is janken (invite or OA)
 *   isJankenInviteEntry()             — Entry is invite link
 *   isJankenResultEntry()             — Entry is OA result
 *   getJankenEntryData()              — Read challenge payload from entry data
 *   hasUnresolvedEntryChallenge()     — Session invite not yet resolved (HUD badge)
 *   hasUnclaimedResultEntry()         — Session OA result not yet claimed (HUD badge)
 *
 * CARE GATE
 *   getJankenBlockedReason()          — Hard-blocked pet states (dead/asleep/daycare)
 *   checkJankenGate(...)              — Block check + care gate (unified)
 *
 * INTERNALS
 *   showResultAndClaim(opts)          — Result popup → reward → action → rematch
 *   processSenderPendingResult(...)   — Single pending: derive → show → claim
 *
 * FLOW DIAGRAMS
 *
 *   Invite entry (receiver resolves challenge):
 *     jankenInviteEntrySequence(origin)
 *       → expiry check → replay guard
 *       → checkJankenGate (block? → modal | care? → care loop)
 *       → deriveOutcome → showResultAndClaim → resolveJankenChallenge
 *       (dismissed/blocked → NOT saved, re-enter from invite link)
 *
 *   OA result entry (sender claims result):
 *     jankenResultEntrySequence(origin)
 *       → find matching pending by challengeId + receiverId
 *       → processSenderPendingResult → claimJankenResult
 *
 *   Create challenge (sender):
 *     jankenSenderSequence(origin)
 *       → FTUE tutorial (if first time)
 *       → generateChallengeId → deriveMove
 *       → openPopup('challenge') → share
 *
 *   HUD button:
 *     jankenHudSequence
 *       → blocked? → modal
 *       → unresolved invite? → jankenInviteEntrySequence('hud')
 *       → unclaimed OA result? → jankenResultEntrySequence('hud')
 *       → jankenSenderSequence('hud')
 *
 *   Result presentation (shared by all result flows):
 *     showResultAndClaim
 *       ├─ careCloseResult? → reuse in-place result
 *       └─ else → open result popup
 *       → hasReward? → open reward popup
 *       → serverAction() → rematch? → jankenSenderSequence
 * ==========================================================================
 */

/** A single element from `getJankenPendingResults`. */
type JankenPendingEntry = ReturnType<typeof getJankenPendingResults>[number];

/** Coerce a raw string to a valid PetSpeciesID, falling back to 'starball'. */
function toValidSpecies(raw: string | undefined): PetSpeciesID {
  if (raw && (petSpeciesTuple as readonly string[]).includes(raw)) {
    return raw as PetSpeciesID;
  }
  return 'starball';
}

/** Get the current user's public profile display name with fallback. */
function getMyDisplayName(): string {
  return getPlayerProfile(user(), user().publicProfile?.name, undefined).name;
}

/** Resolve opponent display info from a pending result, with fallbacks. */
function resolveOpponentInfo(pending: JankenPendingEntry): {
  opponentName: string;
  opponentSpecies: PetSpeciesID;
} {
  return {
    opponentName: getPlayerProfile(
      user(),
      pending.opponentDisplayName,
      undefined,
    ).name,
    opponentSpecies: toValidSpecies(pending.opponentSpecies),
  };
}

// ---------------------------------------------------------------------------
// Entry data helpers
// ---------------------------------------------------------------------------

/**
 * Check if user entered via a janken challenge invite or OA result notification.
 * Checks both `$subFeature` and `subFeature` for compatibility.
 */
export function jankenIsFromEntryData(): boolean {
  const sub = platform.entryData?.$subFeature ?? platform.entryData?.subFeature;
  return (
    JANKEN_SHARE_SUBFEATURES.has(sub) || sub === Feature.INVITE.RESULT_JANKEN
  );
}

/** Check if the entry is an invite link (receiver tapped challenge). */
export function isJankenInviteEntry(): boolean {
  const sub = platform.entryData?.$subFeature ?? platform.entryData?.subFeature;
  return JANKEN_SHARE_SUBFEATURES.has(sub);
}

/** Check if the entry is an OA result notification (sender tapped result). */
export function isJankenResultEntry(): boolean {
  const sub = platform.entryData?.$subFeature ?? platform.entryData?.subFeature;
  return sub === Feature.INVITE.RESULT_JANKEN;
}

/** Read the janken challenge payload from entry data. Returns null if missing. */
export function getJankenEntryData(): {
  challengeId: string;
  senderId: string;
  opponentSpecies?: string;
  opponentDisplayName?: string;
} | null {
  const ed = platform.entryData as Record<string, unknown> | undefined;
  const challengeId = ed?.challengeId as string | undefined;
  const senderId = (platform.entryData?.webReferrer ??
    platform.entryData?.playerID) as string | undefined;

  if (!challengeId || !senderId) return null;

  const opponentSpecies = ed?.senderSpecies as string | undefined;
  const opponentDisplayName = ed?.senderDisplayName as string | undefined;

  return {
    challengeId,
    senderId,
    // Only include optional fields when defined — Replicant rejects explicit undefined.
    ...(opponentSpecies != null && { opponentSpecies }),
    ...(opponentDisplayName != null && { opponentDisplayName }),
  };
}

/**
 * Check if the current session has an unresolved janken challenge from invite entry data.
 * Returns true when the user entered from an invite link (not OA), the challenge is valid
 * (not expired, not self-play), and hasn't been resolved yet.
 *
 * Used by the HUD button to show a notification badge and by the HUD sequence
 * to let the user resolve the challenge without re-entering from the invite link.
 */
export function hasUnresolvedEntryChallenge(): boolean {
  if (isJankenResultEntry()) return false;
  const entry = getJankenEntryData();
  if (!entry) return false;
  if (entry.senderId === user().id) return false;
  if (isJankenChallengeExpired(entry.challengeId, now())) return false;
  if (isResolvedChallenge(user(), entry.challengeId, entry.senderId))
    return false;
  return true;
}

/**
 * Check if the current session has an unclaimed sender result from OA entry data.
 * Returns true when the user entered from an OA result notification and the
 * matching pending result hasn't been claimed yet.
 */
export function hasUnclaimedResultEntry(): boolean {
  if (!isJankenResultEntry()) return false;
  const entry = getJankenEntryData();
  if (!entry) return false;
  if (isJankenChallengeExpired(entry.challengeId, now())) return false;
  // entry.senderId is the receiverId (opponent) for OA entries.
  // Pending result key: ${challengeId}_${receiverId}
  const pending = getJankenPendingResults(user());
  return pending.some(
    (p) =>
      p.challengeId === entry.challengeId && p.receiverId === entry.senderId,
  );
}

// ---------------------------------------------------------------------------
// Sender sequence
// ---------------------------------------------------------------------------

/**
 * Sender sequence: generate challenge → show popup → share.
 *
 * Triggered from HUD button, daily prompt, or "Challenge Back!"/"Rematch!" CTA.
 */
export async function jankenSenderSequence(
  origin: JankenOpenOrigin = 'hud',
): Promise<void> {
  if (!jankenIsAvailable(user())) return;

  // FTUE: auto-show tutorial on first janken open.
  // Mark seen before showing popup to prevent re-show if popup errors.
  if (!jankenHasSeenTutorial(user())) {
    await StateObserver.invoke.setJankenTutorialSeen();
    await openPopupPromise('popupJankenInfo', {});
  }

  track('JankenOpen', { origin });

  // Generate a new challenge ID (nothing saved to state)
  let challengeId: string;
  try {
    challengeId = await StateObserver.invoke.generateJankenChallengeId();
  } catch (e) {
    captureGenericError('jankenGenerateChallengeFailed', e);
    await openPopupPromise('popupError', {
      message: i18n('error.general.tryAgain'),
    });
    return;
  }

  const senderId = user().id;

  // Derive my move — this IS the actual move
  const myMove = deriveJankenSenderMove(challengeId, senderId);

  // Show challenge popup (pet + move preview).
  // Sharing is handled inside the popup — it stays open during/after share.
  await openPopupPromise('popupJanken', {
    mode: 'challenge' as const,
    challengeId,
    challengeType: origin === 'rematch' ? 'rematch' : 'challenge',
    myName: getMyDisplayName(),
    mySpecies: Pet.getters.getCurrentEvolution(user())?.species,
    myMove,
    expiresAt: now() + JANKEN_CHALLENGE_EXPIRY_MS,
  });
}

// ---------------------------------------------------------------------------
// Care gate — careGate
// ---------------------------------------------------------------------------

/**
 * Diagnose why the janken care gate is failing.
 * Returns null when gate passes (both meters > 0 AND no status effects).
 *
 * Called AFTER dead/sleeping are already filtered out upstream in
 * `jankenInviteEntrySequence`. The 'blocked' branch is a defensive fallback
 * for mid-loop state changes (e.g. pet dies while care popup is open).
 *
 * Priority:
 *   1. dead/asleep → block (defensive, should not happen in normal flow)
 *   2. dying → care first_aid (fa_superPerfume only, 100% cure)
 *   3. sick → care first_aid (fa_superCapsule only, 100% cure)
 *   4. tooth_decay → care first_aid (fa_shiningCapsule only, 100% cure)
 *   5. both stomach AND love = 0 → care snack + highlight both meters
 *   6. only stomach = 0 → care food
 *   7. only love = 0 → care toy
 */
type JankenCareGateResult =
  | {
      action: 'blocked';
      reason: 'dead' | 'asleep' | 'wantToGoOut' | 'daycare';
    }
  | {
      action: 'care';
      careType: CareItemType;
      itemFilter?: CareItemID;
      highlightMeters: ('stomach' | 'love')[];
    };

function getJankenCareGate(): JankenCareGateResult | null {
  const state = user();
  const timestamp = now();

  // Defensive: if pet entered a hard-blocked state mid-loop
  const petState = getCurrentPetState(state, timestamp);
  if (
    petState === 'dead' ||
    petState === 'asleep' ||
    petState === 'wantToGoOut'
  ) {
    return { action: 'blocked', reason: petState };
  }
  if (hasPermanentStatusEffect(state, 'daycare', timestamp)) {
    return { action: 'blocked', reason: 'daycare' };
  }

  // Status effects that require specific first-aid items
  if (hasPermanentStatusEffect(state, 'dying', timestamp)) {
    return {
      action: 'care',
      careType: 'first_aid',
      itemFilter: 'fa_superPerfume' as CareItemID,
      highlightMeters: ['stomach', 'love'],
    };
  }
  if (hasPermanentStatusEffect(state, 'sick', timestamp)) {
    return {
      action: 'care',
      careType: 'first_aid',
      itemFilter: 'fa_superCapsule' as CareItemID,
      highlightMeters: ['stomach', 'love'],
    };
  }
  if (hasPermanentStatusEffect(state, 'toothDecay', timestamp)) {
    return {
      action: 'care',
      careType: 'first_aid',
      itemFilter: 'fa_shiningCapsule' as CareItemID,
      highlightMeters: [],
    };
  }

  // Meter conditions
  const failure = jankenGateFailure(state, timestamp);
  if (!failure) return null;

  if (failure.stomachZero && failure.loveZero) {
    return {
      action: 'care',
      careType: 'snack',
      highlightMeters: ['stomach', 'love'],
    };
  }
  if (failure.stomachZero) {
    return {
      action: 'care',
      careType: 'food',
      highlightMeters: ['stomach'],
    };
  }
  return {
    action: 'care',
    careType: 'toy',
    highlightMeters: ['love'],
  };
}

// ---------------------------------------------------------------------------
// Care gate — resolver
// ---------------------------------------------------------------------------

/**
 * Resolve the janken care gate. Opens janken popup in care mode and
 * allows the user to purchase care items in a loop until conditions are met.
 *
 * Flow:
 *   1. Open janken popup in care mode (stays open via NakedPromise pattern)
 *   2. Diagnose current state
 *   3. If blocked (dead/sleeping, defensive) → show modal on top → close janken → return false
 *   4. User presses care CTA → open care popup on top + highlight meters
 *   5. User purchases item via purchaseCareItemSilent (state-only, no animation)
 *   6. If not enough coins → show "play puzzle" modal
 *   7. Re-diagnose: if gate passes → in-place transition to result → return close action
 *   8. If gate still fails → user sees care mode again, can press CTA to retry
 *   9. User dismisses janken popup at any time → return false
 *
 * @returns the PopupJankenClose action from the in-place result transition,
 *          or false if dismissed or blocked
 */
async function jankenResolveCareGate(
  challengeId: string,
  senderId: string,
  receiverId: string,
  opponentName: string,
  opponentSpecies: PetSpeciesID,
): Promise<PopupJankenClose | false> {
  // Open janken popup in care mode
  const popupClosePromise = openPopupPromise('popupJanken', {
    mode: 'care' as const,
    myName: getMyDisplayName(),
    mySpecies: Pet.getters.getCurrentEvolution(user())?.species,
    opponentName,
    opponentSpecies,
    expiresAt:
      (parseJankenChallengeTimestamp(challengeId) ?? now()) +
      JANKEN_CHALLENGE_EXPIRY_MS,
  });

  const popup = getJankenPopupInstance();
  if (!popup) return false;

  // Care loop — user can purchase items and retry until conditions are met
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Wait for care CTA tap OR popup dismiss
    const careTap = popup.awaitCareTap();
    const raceResult = await Promise.race([
      careTap.then(() => 'care' as const),
      popupClosePromise.then(() => 'closed' as const),
    ]);

    if (raceResult === 'closed') return false;

    // User pressed care CTA — re-check to get current care type
    const careGate = getJankenCareGate();

    // Edge case: gate already passes (e.g. decay timer resolved)
    if (!careGate) {
      const outcome = deriveJankenOutcome(challengeId, senderId, receiverId);
      const hasReward =
        outcome.result.receiver === 'win' &&
        canGrantJankenReward(user(), senderId, now());
      await popup.switchToResult({
        myOutcome: outcome.result.receiver,
        myMove: outcome.receiverMove,
        opponentMove: outcome.senderMove,
        hasReward,
        myName: getMyDisplayName(),
        opponentName,
      });
      return await popupClosePromise;
    }

    // Defensive: pet entered a blocked state mid-loop
    if (careGate.action === 'blocked') {
      if (PopupMonitor.isOpen('popupJanken')) {
        closePopup('popupJanken', 'dismissed');
      }
      await popupClosePromise;
      await openPopupPromise('popupModalWhite', {
        title: i18n('janken.careGate'),
        message: i18n(`janken.blocked.${careGate.reason}`),
        tracking: {
          feature: Feature.JANKEN._,
          $subFeature: Feature.JANKEN.BLOCKED,
        },
      });
      return false;
    }

    // Open care popup on top (no highlight yet — avoids z-index conflicts)
    const careItemId = await openPopupPromise('popupSelectCareItem', {
      type: careGate.careType,
      itemFilter: careGate.itemFilter,
      additionalTracking: { itemType: careGate.careType },
    });

    if (careItemId) {
      try {
        const result = await purchaseCareItemSilent(careItemId, {
          highlightMeters: careGate.highlightMeters,
        });

        if (result.notEnoughCoins) {
          await openPopupPromise('popupModalWhite', {
            title: i18n('janken.careGate'),
            message: i18n('janken.notEnoughCoins'),
            tracking: {
              feature: Feature.JANKEN._,
              $subFeature: Feature.JANKEN.NOT_ENOUGH_COINS,
            },
          });
        }
      } catch (e) {
        captureGenericError('jankenCareGatePurchaseFailed', e);
      }
    }

    // Re-check after purchase to determine next step
    const postCareGate = getJankenCareGate();

    // Defensive: pet entered a blocked state during care popup
    if (postCareGate?.action === 'blocked') {
      if (PopupMonitor.isOpen('popupJanken')) {
        closePopup('popupJanken', 'dismissed');
      }
      await popupClosePromise;
      await openPopupPromise('popupModalWhite', {
        title: i18n('janken.careGate'),
        message: i18n(`janken.blocked.${postCareGate.reason}`),
        tracking: {
          feature: Feature.JANKEN._,
          $subFeature: Feature.JANKEN.BLOCKED,
        },
      });
      return false;
    }

    // Check if ALL conditions now pass (meters AND no status effects)
    if (!postCareGate) {
      const outcome = deriveJankenOutcome(challengeId, senderId, receiverId);
      const hasReward =
        outcome.result.receiver === 'win' &&
        canGrantJankenReward(user(), senderId, now());
      await popup.switchToResult({
        myOutcome: outcome.result.receiver,
        myMove: outcome.receiverMove,
        opponentMove: outcome.senderMove,
        hasReward,
        myName: getMyDisplayName(),
        opponentName,
      });
      return await popupClosePromise;
    }

    // Gate still fails → loop back to awaitCareTap
  }
}

// ---------------------------------------------------------------------------
// Shared blocked-state popup
// ---------------------------------------------------------------------------

type JankenBlockedReason = Extract<
  JankenCareGateResult,
  { action: 'blocked' }
>['reason'];

/** Check if pet is in a hard-blocked state that prevents janken entirely. */
export function getJankenBlockedReason(): JankenBlockedReason | null {
  const petState = getCurrentPetState(user(), now());
  if (hasPermanentStatusEffect(user(), 'daycare', now())) return 'daycare';
  if (
    petState === 'dead' ||
    petState === 'asleep' ||
    petState === 'wantToGoOut'
  ) {
    return petState;
  }
  return null;
}

/** Show modal popup for a janken-blocked pet state. */
async function showJankenBlockedPopup(
  reason: JankenBlockedReason,
): Promise<void> {
  await openPopupPromise('popupModalWhite', {
    title: i18n('janken.careGate'),
    message: i18n(`janken.blocked.${reason}`),
    tracking: {
      feature: Feature.JANKEN._,
      $subFeature: Feature.JANKEN.BLOCKED,
    },
  });
}

// ---------------------------------------------------------------------------
// Shared gate check (blocking states + care gate)
// ---------------------------------------------------------------------------

/**
 * Check blocking pet states and run the care gate if needed.
 * Shared by `jankenInviteEntrySequence` (immediate) and deferred pending result flows
 * (deferred receiver pending results).
 *
 * @returns
 *   - `{ status: 'blocked', reason }` — pet is in a hard-blocked state
 *   - `{ status: 'dismissed' }` — care gate was needed but user dismissed
 *   - `{ status: 'passed', careCloseResult }` — gate passed (false = no care needed,
 *      PopupJankenClose = care gate showed result in-place)
 */
type JankenGateResult =
  | { status: 'blocked'; reason: JankenBlockedReason }
  | { status: 'dismissed' }
  | { status: 'passed'; careCloseResult: PopupJankenClose | false };

async function checkJankenGate(
  challengeId: string,
  senderId: string,
  receiverId: string,
  opponentName: string,
  opponentSpecies: PetSpeciesID,
): Promise<JankenGateResult> {
  // Blocking pet states — cannot play janken at all
  const blockedReason = getJankenBlockedReason();
  if (blockedReason) {
    return { status: 'blocked', reason: blockedReason };
  }

  // Care gate — dying/sick/toothDecay or low meters
  const petState = getCurrentPetState(user(), now());
  if (
    !jankenGatePasses(user(), now()) ||
    petState === 'dying' ||
    petState === 'sick' ||
    petState === 'toothDecay'
  ) {
    const careCloseResult = await jankenResolveCareGate(
      challengeId,
      senderId,
      receiverId,
      opponentName,
      opponentSpecies,
    );
    if (!careCloseResult) return { status: 'dismissed' };
    return { status: 'passed', careCloseResult };
  }

  return { status: 'passed', careCloseResult: false };
}

// ---------------------------------------------------------------------------
// Shared result presenter + claim/resolve
// ---------------------------------------------------------------------------

interface ShowResultOpts {
  challengeId: string;
  senderId: string;
  receiverId: string;
  myOutcome: JankenOutcome;
  myMove: JankenMove;
  opponentMove: JankenMove;
  hasReward: boolean;
  opponentName: string;
  opponentSpecies: PetSpeciesID;
  careCloseResult?: PopupJankenClose | false;
  serverAction: () => Promise<void>;
  origin: JankenOpenOrigin;
  skipAnimation?: boolean;
}

/**
 * Show janken result popup, reward popup if won, call server action, handle rematch.
 * Shared by all three result flows (immediate receiver, deferred receiver, sender).
 */
async function showResultAndClaim(
  opts: ShowResultOpts,
): Promise<PopupJankenClose> {
  const { careCloseResult, hasReward, serverAction } = opts;

  const resultPopupArgs = {
    mode: 'result' as const,
    myOutcome: opts.myOutcome,
    myMove: opts.myMove,
    opponentMove: opts.opponentMove,
    hasReward,
    myName: getMyDisplayName(),
    mySpecies: Pet.getters.getCurrentEvolution(user())?.species,
    opponentName: opts.opponentName,
    opponentSpecies: opts.opponentSpecies,
    expiresAt:
      (parseJankenChallengeTimestamp(opts.challengeId) ?? now()) +
      JANKEN_CHALLENGE_EXPIRY_MS,
    skipAnimation: opts.skipAnimation ?? false,
  };

  let closeResult: PopupJankenClose;
  if (careCloseResult) {
    closeResult = careCloseResult;
  } else {
    closeResult = await openPopupPromise('popupJanken', resultPopupArgs);
  }

  if (closeResult === 'dismissed') return closeResult;

  // Show reward popup after user clicks "Claim Reward" CTA or closes (X)
  // after seeing the result. 'claim' = CTA click, 'closed' = X after result shown.
  if ((closeResult === 'claim' || closeResult === 'closed') && hasReward) {
    const reward = rollJankenReward(
      opts.challengeId,
      opts.senderId,
      opts.receiverId,
    );
    const wantsChallengeBack = await openPopupPromise('popupJankenReward', {
      reward,
      outcome: opts.myOutcome,
      feature: Feature.JANKEN._,
      subFeature: Feature.JANKEN.WIN_REWARD,
    });
    closeResult = wantsChallengeBack ? 'challengeBack' : 'claim';
  }

  await serverAction();

  if (closeResult === 'challengeBack' || closeResult === 'rematch') {
    await jankenSenderSequence('rematch');
  }

  return closeResult;
}

// ---------------------------------------------------------------------------
// Receiver sequence
// ---------------------------------------------------------------------------

/**
 * Invite entry sequence: expiry → replay → blocking check → care gate → result → resolve.
 *
 * Triggered from launch sequence or HUD when user entered via invite link.
 */
export async function jankenInviteEntrySequence(
  origin: JankenOpenOrigin,
): Promise<void> {
  const entryData = getJankenEntryData();
  if (!entryData) return;

  const { challengeId, senderId, opponentSpecies, opponentDisplayName } =
    entryData;
  const receiverId = user().id;

  const opponentName =
    opponentDisplayName ?? getPlayerProfile(user(), undefined, undefined).name;
  const validSpecies = toValidSpecies(opponentSpecies);

  // 0. Self-play guard — action no-ops on self-play
  if (senderId === receiverId) return;

  // 1. Expiry check
  if (isJankenChallengeExpired(challengeId, now())) {
    track('JankenExpired', { challengeId });
    await openPopupPromise('popupModalWhite', {
      title: i18n('janken.title'),
      message: i18n('janken.expired'),
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.EXPIRED,
      },
    });
    return;
  }

  // 2. Replay check
  if (isResolvedChallenge(user(), challengeId, senderId)) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('janken.title'),
      message: i18n('janken.alreadyPlayed'),
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.ALREADY_PLAYED,
      },
    });
    return;
  }

  // 3–4. Blocking pet state check + care gate
  const gate = await checkJankenGate(
    challengeId,
    senderId,
    receiverId,
    opponentName,
    validSpecies,
  );
  if (gate.status === 'blocked') {
    track('JankenBlocked', { challengeId, reason: gate.reason });
    await showJankenBlockedPopup(gate.reason);
    return;
  }
  if (gate.status === 'dismissed') return;
  const careCloseResult = gate.careCloseResult;

  // 5. Derive both moves
  const { senderMove, receiverMove, result } = deriveJankenOutcome(
    challengeId,
    senderId,
    receiverId,
  );

  // 6. Snapshot reward eligibility BEFORE the action mutates state
  const hasReward =
    result.receiver === 'win' && canGrantJankenReward(user(), senderId, now());

  // 7. Track reveal event
  track('JankenReveal', {
    challengeId,
    senderMove,
    receiverMove,
    outcome: result.receiver,
    isReward: hasReward,
  });

  // 8–10. Show result popup, reward popup, resolve, handle rematch
  await showResultAndClaim({
    challengeId,
    senderId,
    receiverId,
    myOutcome: result.receiver,
    myMove: receiverMove,
    opponentMove: senderMove,
    hasReward,
    opponentName,
    opponentSpecies: validSpecies,
    careCloseResult,
    origin,
    serverAction: async () => {
      try {
        await StateObserver.invoke.resolveJankenChallenge({
          challengeId,
          senderId,
        });
      } catch (e) {
        captureGenericError('jankenResolveChallengeFailed', e);
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Process a single sender pending result
// ---------------------------------------------------------------------------

/** Process a sender pending result: derive → show → reward → claim. */
async function processSenderPendingResult(
  pending: JankenPendingEntry,
  origin: JankenOpenOrigin,
): Promise<boolean> {
  const myId = user().id;
  const { key, challengeId, receiverId } = pending;
  const { opponentName, opponentSpecies } = resolveOpponentInfo(pending);

  // Derive full outcome (myId is the sender)
  const { senderMove, receiverMove, result } = deriveJankenOutcome(
    challengeId,
    myId,
    receiverId,
  );

  // Snapshot reward eligibility BEFORE the action mutates state
  const hasReward =
    result.sender === 'win' && canGrantJankenReward(user(), receiverId, now());

  // Track result event
  track('JankenResult', {
    challengeId,
    outcome: result.sender,
    isReward: hasReward,
  });

  // Show result popup, reward popup, claim, handle rematch
  await showResultAndClaim({
    challengeId,
    senderId: myId,
    receiverId,
    myOutcome: result.sender,
    myMove: senderMove,
    opponentMove: receiverMove,
    hasReward,
    opponentName,
    opponentSpecies,
    origin,
    skipAnimation: true,
    serverAction: async () => {
      try {
        await StateObserver.invoke.claimJankenResult({ pendingResultId: key });
      } catch (e) {
        captureGenericError('jankenClaimResultFailed', e);
      }
    },
  });

  return true;
}

// ---------------------------------------------------------------------------
// Result sequence (pending results loop)
// ---------------------------------------------------------------------------

/**
 * Process the sender pending result matching the OA entry data.
 * Shows only the specific result the user tapped on, not all pending results.
 *
 * @returns true if a matching result was found and processed.
 */
export async function jankenResultEntrySequence(
  origin: JankenOpenOrigin,
): Promise<boolean> {
  const entry = getJankenEntryData();
  if (!entry) return false;

  const pendingResults = getJankenPendingResults(user());
  // entry.senderId is the receiverId for OA entries (webReferrer = receiverId)
  const match = pendingResults.find(
    (p) =>
      p.challengeId === entry.challengeId && p.receiverId === entry.senderId,
  );
  if (!match) return false;
  if (isJankenChallengeExpired(match.challengeId, now())) return false;

  await processSenderPendingResult(match, origin);
  return true;
}

// ---------------------------------------------------------------------------
// HUD button sequence
// ---------------------------------------------------------------------------

/**
 * HUD button sequence: resolve unresolved entry challenge → drain sender-pending
 * results → open sender sequence.
 *
 * Triggered from the janken HUD button on the home screen.
 */
export async function jankenHudSequence(): Promise<void> {
  // Block if pet is in a hard-blocked state
  const blockedReason = getJankenBlockedReason();
  if (blockedReason) {
    await showJankenBlockedPopup(blockedReason);
    return;
  }

  let shownPopup = false;

  // Resolve unresolved invite challenge from this session
  if (hasUnresolvedEntryChallenge()) {
    await jankenInviteEntrySequence('hud');
    shownPopup = true;
  }

  // Claim unclaimed OA result from this session
  if (hasUnclaimedResultEntry()) {
    const shown = await jankenResultEntrySequence('hud');
    if (shown) shownPopup = true;
  }

  if (!shownPopup) {
    await jankenSenderSequence('hud');
  }
}
